import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import useAuth from 'hooks/useAuth';
import Layout from '../../../layouts';
import Page from '../../../components/Page';
import axios from '../../../utils/axios';
import { PATH_DASHBOARD } from '../../../routes/paths';
import { isMaster, podeGravarCadastroAdmin } from '../../../utils/perfil';
import { Num, PillStatus } from '../../../components/gps';
import PainelLista, { Coluna } from '../../../components/gps/PainelLista';
import MenuAcoes, { ResumoExclusao } from '../../../components/gps/MenuAcoes';
import Modal, { BotaoSecundario } from '../../../components/gps/Modal';
import ModalPolitica from '../../../components/gps/ModalPolitica';
import FiltroRegional from '../../../components/gps/FiltroRegional';
import { linhasIniciais } from '../../../components/gps/paginacao';

/* ============================================================================
   Politicas - visual novo (shadcn + Tailwind).

   ESTA TELA NAO GRAVA NADA, e a restricao aqui e mais forte que nas outras:
   politica e o unico cadastro que sai daqui e vai para o Google, e de la para
   dispositivo real em campo. Listar e navegar e o escopo; criar e editar seguem
   na pagina existente (/dashboard/politicas/new/:name), intacta.

   Contrato conferido em remoteAPI/src/app/politicas/politicas.service.ts:102:
     GET /politicas/paginado?page&rows&valor&campo&direcao&ordenar&status&regional
     -> { content: PoliticaEntity[] com a relacao `entidade`, totalElements }

   DETALHE QUE JA CAUSOU BUG EM OUTRA TELA: aqui o service NAO converte o status
   para o nome do enum (diferente de apks e users, que devolvem "ATIVO"). Chega
   o NUMERO. `ehAtivo` aceita os dois formatos de proposito - o dia em que o
   backend padronizar, esta tela nao quebra.

   `campo` e `ordenar` viram propriedade do QueryBuilder, entao so podem sair das
   constantes daqui; texto do usuario nunca vai para esses dois parametros.
   ============================================================================ */

type Politica = {
  name?: string;
  nome?: string;
  status?: number | string;
  kiosk?: string;
  entidade?: { nome?: string } | null;
  createdAt?: string;
};

const STATUS = { TODOS: -1, ATIVO: 1, INATIVO: 0 } as const;

function numero(valor: unknown) {
  const n = Number(valor);
  return Number.isFinite(n) ? n : 0;
}

function ehAtivo(v: unknown) {
  return String(v).toUpperCase() === 'ATIVO' || Number(v) === 1;
}

Politicas.getLayout = function getLayout(page: React.ReactElement) {
  return <Layout>{page}</Layout>;
};

export default function Politicas() {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [menuAberto, setMenuAberto] = useState<string | null>(null);
  /* `null` = fechado. `{nome}` = editar; `{copiarDe}` = duplicar. Um estado so,
     porque as duas coisas abrem o MESMO modal com parametros diferentes. */
  const [editando, setEditando] = useState<{ nome?: string; copiarDe?: string } | null>(null);
  const [apagando, setApagando] = useState<Politica | null>(null);
  const [removendo, setRemovendo] = useState(false);
  const [linhas, setLinhas] = useState<Politica[]>([]);
  const [total, setTotal] = useState(0);
  // Recarrega os cartoes (Total/Ativos) depois de apagar ou salvar - antes so na montagem.
  const [versaoIndices, setVersaoIndices] = useState(0);
  const [pagina, setPagina] = useState(0);
  const [busca, setBusca] = useState('');
  /* ATIVOS por padrao, e nao "Todos" - pedido de 25/08/2026.

     O trabalho do dia a dia acontece sobre o que esta ativo; os inativos sao
     historico. Abrindo em "Todos", a primeira pagina vinha misturada e era preciso
     filtrar toda vez para chegar ao que interessa.

     Os CONTADORES dos tres segmentos continuam vindo completos (vem de endpoint
     proprio de indices), entao o total geral segue visivel e "Todos" e um clique. */
  const [status, setStatus] = useState<number>(STATUS.ATIVO);
  const [porGrupo, setPorGrupo] = useState(false);
  const [linhasPorPagina, setLinhasPorPagina] = useState(linhasIniciais);
  const [regional, setRegional] = useState<number | null>(null);
  // `ordenar` vira propriedade do QueryBuilder (`politica.${ordenar}`), mais o
  // caso especial 'Entidade', que o service traduz para entidade.nome.
  const [ordenarPor, setOrdenarPor] = useState('nome');
  const [direcao, setDirecao] = useState<'asc' | 'desc'>('asc');
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [indices, setIndices] = useState({ total: 0, ativos: 0, inativos: 0 });

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      // O backend trata `campo === 'entidade'` como caso especial (busca em
      // entidade.nome); qualquer outro valor vira propriedade da propria
      // politica. Por isso o alternador e um booleano, nao texto livre.
      const campo = porGrupo ? 'entidade' : 'nome';

      const resposta = await axios.get<{ content: Politica[]; totalElements: number }>(
        `/politicas/paginado?page=${pagina}&rows=${linhasPorPagina}` +
          `&valor=${encodeURIComponent(busca.trim())}&campo=${campo}` +
          `&direcao=${direcao}&ordenar=${ordenarPor}&status=${status}&regional=${regional ?? 'null'}`
      );
      setLinhas(resposta.data?.content || []);
      setTotal(numero(resposta.data?.totalElements));
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Não foi possível carregar as políticas.');
      setLinhas([]);
      setTotal(0); // sem isto o rodape seguia "Mostrando 0 de N" com a paginacao ativa
    } finally {
      setCarregando(false);
    }
  }, [pagina, busca, status, porGrupo, linhasPorPagina, regional, ordenarPor, direcao]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    axios
      .get<any>('/politicas/findByStatus')
      .then((r) => {
        const d = r.data || {};
        setIndices({ total: numero(d.total), ativos: numero(d.ativo), inativos: numero(d.inativo) });
      })
      .catch(() => setIndices({ total: 0, ativos: 0, inativos: 0 }));
  }, [versaoIndices]);

  const colunas: Array<Coluna<Politica>> = useMemo(
    () => [
      {
        chave: 'nome',
        ordenarPor: 'nome',
        exportar: (p) => p.nome ?? p.name ?? '',
        rotulo: 'Política',
        largura: 'minmax(220px,2.4fr)',
        esqueleto: 'w-44',
        render: (p) => (
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="block truncate text-[12.5px] font-semibold">{p.nome || p.name || ' - '}</span>
          </div>
        ),
      },
      {
        chave: 'grupo',
        ordenarPor: 'Entidade',
        exportar: (p) => p.entidade?.nome ?? '',
        rotulo: 'Grupo',
        largura: 'minmax(160px,1.6fr)',
        esqueleto: 'w-36',
        render: (p) => (
          <span className="block truncate text-[12px] text-muted-foreground">{p.entidade?.nome || '—'}</span>
        ),
      },
      {
        chave: 'modo',
        exportar: (p) => (p.kiosk ? 'KIOSK' : 'padrão'),
        rotulo: 'Modo',
        largura: '110px',
        esqueleto: 'w-14',
        render: (p) =>
          p.kiosk ? (
            <span className="inline-flex h-[22px] items-center rounded-md bg-soft px-2 text-[11px] font-bold tracking-wide text-muted-foreground">
              KIOSK
            </span>
          ) : (
            <span className="text-[12px] text-faint">padrão</span>
          ),
      },
      {
        chave: 'identificador',
        ordenarPor: 'name',
        exportar: (p) => p.name ?? '',
        rotulo: 'Identificador Google',
        largura: 'minmax(160px,1.4fr)',
        esqueleto: 'w-40',
        render: (p) => (
          <span className="block truncate text-[12px] text-muted-foreground" title={p.name}>
            {p.name || '—'}
          </span>
        ),
      },
      {
        chave: 'status',
        ordenarPor: 'status',
        exportar: (p) => (ehAtivo(p.status) ? 'ATIVA' : 'INATIVA'),
        rotulo: 'Status',
        largura: '110px',
        esqueleto: 'w-16',
        render: (p) => <PillStatus ativo={ehAtivo(p.status)} />,
      },
      {
        chave: 'acao',
        rotulo: 'Opções',
        largura: '80px',
        alinhar: 'dir',
        esqueleto: 'nenhum',
        // Mesmas acoes do menu antigo, inclusive DUPLICAR, que abre o formulario
        // de criacao com `?copiarDe=` - e o caminho pelo qual politica nova
        // nasce, ja que criar do zero esta desabilitado de proposito.
        render: (p) => (
          <MenuAcoes<Politica>
            linha={p}
            aberto={menuAberto === p.name}
            onAlternar={() => setMenuAberto(menuAberto === p.name ? null : p.name ?? null)}
            onFechar={() => setMenuAberto(null)}
            ancorarADireita
            acoes={[
              {
                // Abre o MODAL, sem sair da lista: ao salvar o usuario volta para o
                // mesmo lugar, com o mesmo filtro e a mesma pagina - antes ele caia
                // numa rota nova e perdia o contexto da busca.
                rotulo: 'Abrir / editar',
                ao: (x) => x.name && setEditando({ nome: x.name }),
              },
              {
                rotulo: 'Duplicar',
                ao: (x) => x.name && setEditando({ copiarDe: x.name }),
              },
              {
                rotulo: 'Apagar',
                perigo: true,
                /* SO MASTER (10/09/2026, a pedido).

                   Estava em `podeGravarCadastroAdmin`, que inclui Administrador - o
                   mesmo teste usado para CRIAR e EDITAR. Apagar nao pertence a essa
                   categoria: criar errado se corrige editando, apagar nao tem desfazer
                   e leva junto os vinculos do registro.

                   A autoridade continua sendo o backend; isto so evita mostrar uma
                   opcao que a pessoa nao deveria acionar. */
                visivel: isMaster(user),
                ao: (x) => setApagando(x),
              },
            ]}
          />
        ),
      },
    ],
    [menuAberto, user]
  );

  const apagar = async () => {
    if (!apagando?.name) return;
    setRemovendo(true);
    try {
      await axios.delete(`/politicas/${encodeURIComponent(apagando.name)}`);
      enqueueSnackbar('Política apagada.', { variant: 'success' });
      setApagando(null);
      setVersaoIndices((v) => v + 1);
      // Era o unico item de uma pagina que nao e a primeira: volta uma pagina.
      // Sem isto a tela ficava em "Nenhum ... encontrado" com registros antes.
      if (linhas.length === 1 && pagina > 0) setPagina((p) => p - 1);
      else await carregar();
    } catch (e: any) {
      // O backend recusa apagar politica com dispositivo vinculado, e tambem
      // remove a policy no Google - a mensagem dele e mais util que a nossa.
      enqueueSnackbar(e?.response?.data?.message || 'Não foi possível apagar a política.', { variant: 'error' });
    } finally {
      setRemovendo(false);
    }
  };

  // SEM botao "Nova politica" de proposito: na tela antiga esse botao esta
  // COMENTADO (politicas/index.tsx:342-347). Politica nasce vinculada a um grupo;
  // criar solta gera registro orfao que nao chega a aparelho nenhum. Reintroduzir
  // o botao aqui seria reabrir um caminho que alguem fechou de proposito.
  return (
    <Page title="Políticas">
      <PainelLista<Politica>
        titulo="Políticas"
        descricao={
          <>
            <Num>{(indices.total || total).toLocaleString('pt-BR')}</Num> políticas. Cada uma é enviada ao
            Google e aplicada nos aparelhos do grupo vinculado — alterar aqui muda dispositivo em campo.
            <br />
            Não se cria política por aqui: ela nasce junto com o grupo, na tela de Grupos.
          </>
        }
        /* O AVISO "politica nova e criada a partir do grupo" SAIU DAQUI (27/08/2026).

           O slot `acao` e onde vive o botao primario da tela - "+ Novo usuario",
           "+ Novo grupo". Um texto com moldura naquele lugar tem a forma de botao e
           nao e clicavel: quem chega procurando como criar tenta clicar nele.

           A informacao continua na tela, na dica do titulo (`descricao`), que e onde
           mora a explicacao de cada lista. E ali ela responde melhor: aparece quando
           a pessoa procura contexto, e nao ocupando o lugar de uma acao que esta
           tela deliberadamente nao tem. */
        indicadores={[
          { rotulo: 'Total', valor: indices.total || total, legenda: 'cadastradas', tom: 'info' },
          {
            rotulo: 'Ativas',
            valor: indices.ativos,
            legenda: 'aplicadas',
            tom: 'ok',
            base: indices.total || total,
          },
          {
            rotulo: 'Inativas',
            valor: indices.inativos,
            legenda: 'sem uso',
            tom: 'danger',
            base: indices.total || total,
          },
        ]}
        busca={busca}
        aoBuscar={(v) => {
          setPagina(0);
          setBusca(v);
        }}
        placeholderBusca={porGrupo ? 'Nome do grupo' : 'Nome da política'}
        segmentos={[
          { valor: STATUS.TODOS as number, rotulo: 'Todas', total: indices.total || total },
          { valor: STATUS.ATIVO as number, rotulo: 'Ativas', total: indices.ativos },
          { valor: STATUS.INATIVO as number, rotulo: 'Inativas', total: indices.inativos },
        ]}
        segmentoAtual={status}
        aoTrocarSegmento={(v) => {
          setPagina(0);
          setStatus(v);
        }}
        filtrosExtras={
          <>
          <FiltroRegional valor={regional} onChange={(v) => { setPagina(0); setRegional(v); }} />
          <label className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-lg border border-line bg-surface px-3 text-[12px] font-semibold text-muted-foreground">
            <input
              type="checkbox"
              checked={porGrupo}
              onChange={(e) => {
                setPagina(0);
                setPorGrupo(e.target.checked);
              }}
              className="h-3.5 w-3.5 accent-[hsl(var(--primary))]"
            />
            Buscar pelo grupo
          </label>
          </>
        }
        ordenarPor={ordenarPor}
        direcao={direcao}
        aoOrdenar={(campo) => {
          if (campo === ordenarPor) setDirecao((v) => (v === 'asc' ? 'desc' : 'asc'));
          else { setOrdenarPor(campo); setDirecao('asc'); }
          setPagina(0);
        }}
        linhasPorPagina={linhasPorPagina}
        aoTrocarLinhasPorPagina={(n) => { setPagina(0); setLinhasPorPagina(n); }}
        aoLimparFiltros={() => { setBusca(''); setStatus(STATUS.TODOS); setPorGrupo(false); setRegional(null); setOrdenarPor('nome'); setDirecao('asc'); setPagina(0); }}
        nomeArquivo="politicas"
        colunas={colunas}
        linhas={linhas}
        chaveLinha={(p, i) => p.name || `sem-nome-${i}`}
        aoClicarLinha={(p) => p.name && setEditando({ nome: p.name })}
        carregando={carregando}
        erro={erro}
        aoRecarregar={carregar}
        vazio="Nenhuma política encontrada com esse filtro."
        pagina={pagina}
        totalPaginas={Math.max(1, Math.ceil(total / linhasPorPagina))}
        total={total}
        aoTrocarPagina={setPagina}
        rotuloPlural="políticas"
      />

      <Modal
        aberto={Boolean(apagando)}
        titulo="Apagar política?"
        descricao="A policy também é removida no Google. Política com dispositivo vinculado é recusada."
        icone="!"
        largura="max-w-[480px]"
        onFechar={() => setApagando(null)}
        rodape={
          <>
            <BotaoSecundario onClick={() => setApagando(null)} disabled={removendo}>
              Cancelar
            </BotaoSecundario>
            <button
              type="button"
              onClick={apagar}
              disabled={removendo}
              className="h-9 rounded-lg bg-danger px-4 text-[12.5px] font-bold text-white disabled:opacity-40"
            >
              {removendo ? 'Apagando...' : 'Apagar política'}
            </button>
          </>
        }
      >
        {apagando && (
          <ResumoExclusao
            titulo={apagando.nome || apagando.name || '—'}
            detalhe={apagando.name}
            extra={
              <>
                Grupo: <strong className="text-ink">{apagando.entidade?.nome || '—'}</strong>
              </>
            }
          />
        )}
      </Modal>

      {/* Formulario da politica. Montado SO quando ha algo para editar: ele carrega
          a politica, os aplicativos e os sites ao abrir, e manter isso montado a
          espera de um clique custaria tres requisicoes por visita a lista. */}
      {editando && (
        <ModalPolitica
          aberto
          nome={editando.nome}
          copiarDe={editando.copiarDe}
          onFechar={() => setEditando(null)}
          onSalvo={() => {
            setVersaoIndices((v) => v + 1);
            // Recarrega a lista para o nome/status/grupo alterados aparecerem sem
            // F5, e fecha. Quem duplicou volta com a copia ja na listagem.
            carregar();
            setEditando(null);
          }}
        />
      )}
    </Page>
  );
}
